import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CartPageLayoutHandler } from '@spartacus/cart/base/components';
import { CartAdapter } from '@spartacus/cart/base/core';
import { CmsConfig, ConfigModule, I18nConfig, I18nModule, OccConfig, UrlModule, provideConfig, provideDefaultConfig } from '@spartacus/core';
import { IconModule, LayoutConfig, SpinnerModule } from '@spartacus/storefront';
import { CartBaseFeatureModule } from '../../spartacus/features/cart/cart-base-feature.module';
import { UigLegalModule } from '../uig-legal/uig-legal.module';
import { UigMarketplaceIconModule } from '../uig-product/uig-marketplace-icon/uig-marketplace-icon.module';
import { UigAddToSavedCartModule } from './uig-add-to-saved-cart/uig-add-to-saved-cart.module';
import { UigCartDetailsModule } from './uig-cart-details/uig-cart-details.module';
import { UigCartPageLayoutHandler } from './uig-cart-page-layout-handler';
import { UigCartSharedModule } from './uig-cart-shared/uig-cart-shared.module';
import { UigCartTotalsModule } from './uig-cart-totals/uig-cart-totals.module';
import { UigEmptyCartComponent } from './uig-empty-cart/uig-empty-cart.component';
import { UigFastEntryComponent } from './uig-fast-entry/uig-fast-entry.component';
import { UigMiniCartModule } from './uig-mini-cart/uig-mini-cart.module';
import { UigOccCartAdapter } from './uig-occ-cart.adapter';
import { UigProductImportComponent } from './uig-product-import/uig-product-import.component';
import { UigSavedCartModule } from './uig-saved-cart/uig-saved-cart.module';
import { UigShippingNoteModule } from './uig-shipping-note/uig-shipping-note.module';
import { UigWishlistComponent } from './uig-wishlist/uig-wishlist.component';

@NgModule({
  declarations: [UigFastEntryComponent, UigWishlistComponent, UigProductImportComponent, UigEmptyCartComponent],
  imports: [
    CartBaseFeatureModule,
    UigCartDetailsModule,
    UigCartTotalsModule,
    UigCartSharedModule,
    UigShippingNoteModule,
    UigSavedCartModule,
    UigAddToSavedCartModule,
    UigLegalModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    I18nModule,
    SpinnerModule,
    UigMiniCartModule,
    UrlModule,
    RouterModule,
    IconModule,
    UigMarketplaceIconModule,
    ConfigModule.withConfig({
      backend: {
        occ: {
          endpoints: {
            customAddFastEntry: '/cart/add/direct',
            customAddFromWishlist: '/cart/add/all',
            customUpdateCartEntryNote: '/cart/update/${entryNumber}/note',
            cart: 'users/${userId}/carts/${cartId}?fields=DEFAULT,invoiceAddress(FULL),purchaseOrderNumber,deliveryTicket,potentialProductPromotions,appliedProductPromotions,potentialOrderPromotions,appliedOrderPromotions,entries(backendMessages,note,totalPrice(formattedValue),product(dropShipmentExcluded,images(FULL),stock(FULL),minDecline,bulkyGoods,deliveryCostBulkyDirect(formattedValue),ekPrice(FULL)),basePrice(formattedValue,value),updateable),totalPrice(formattedValue),totalItems,totalPriceWithTax(formattedValue),totalDiscounts(value,formattedValue),subTotal(formattedValue),deliveryItemsQuantity,deliveryCost(formattedValue),totalTax(formattedValue, value),pickupItemsQuantity,net,appliedVouchers,productDiscounts(formattedValue),user,saveTime,name,description,activeCartForUser,isOwner,publicCart,deliveryCostDirectTotal(formattedValue),deliveryCostBulkyDirect(formattedValue),deliveryCostDirect(formattedValue)',
            carts: 'users/${userId}/carts?fields=carts(DEFAULT,publicCart,isOwner,activeCartForUser,potentialProductPromotions,appliedProductPromotions,potentialOrderPromotions,appliedOrderPromotions,entries(totalPrice(formattedValue),product(images(FULL),stock(FULL),bulkyGoods,deliveryCostBulkyDirect(formattedValue)),basePrice(formattedValue,value),updateable,note),totalPrice(formattedValue),totalItems,totalPriceWithTax(formattedValue),totalDiscounts(value,formattedValue),subTotal(formattedValue),deliveryItemsQuantity,deliveryCost(formattedValue),totalTax(formattedValue, value),pickupItemsQuantity,net,appliedVouchers,productDiscounts(formattedValue),user,saveTime,name,description,deliveryCostBulkyDirect(formattedValue),deliveryCostDirect(formattedValue),deliveryCostDirectTotal(formattedValue))',
            ImportProductsUpload: '/cart/upload',
            updatePublicCartFlag: '/cart/updatePublicFlag',
            savedCarts: '/users/${userId}/carts?savedCartsOnly=true&fields=carts(DEFAULT,publicCart,isOwner,activeCartForUser,potentialProductPromotions,appliedProductPromotions,potentialOrderPromotions,appliedOrderPromotions,entries(totalPrice(formattedValue),product(images(FULL),stock(FULL)),basePrice(formattedValue,value),updateable),totalPrice(formattedValue),totalItems,totalPriceWithTax(formattedValue),totalDiscounts(value,formattedValue),subTotal(formattedValue),deliveryItemsQuantity,deliveryCost(formattedValue),totalTax(formattedValue, value),pickupItemsQuantity,net,appliedVouchers,productDiscounts(formattedValue),saveTime,user,name,description)',
            cartDownload: '/cart/download',
            removeAllEntries: '/cart/removeAllEntries'
          },
        },
      },
      layoutSlots: {
        CartPageTemplate: {
          slots: [
            'TopContent',
            'CenterLeftContentSlot',
            'EmptyCartMiddleContent',
            'CenterRightContentSlot',
            'BottomContentSlot'
          ]
        },
      },
    } as OccConfig | LayoutConfig)
  ],
  exports: [
    UigCartDetailsModule,
    UigCartTotalsModule,
    UigCartSharedModule
  ],
  providers: [
    {
      provide: CartAdapter,
      useClass: UigOccCartAdapter,
    },
    provideDefaultConfig({
      cmsComponents: {
        CartWishlistComponent: {
          component: UigWishlistComponent
        },
        CartFastEntryComponent: {
          component: UigFastEntryComponent
        },
        CartCsvImportComponent: {
          component: UigProductImportComponent
        }
      }
    } as CmsConfig | LayoutConfig),
    provideConfig({
      i18n: {
        chunks: {
          uigCart: ['fastEntry', 'cartWishlist', 'notes', 'shippingNote', 'uigCartDetails', 'uigCartItems', 'productImport', 'cartExport', 'addToCartLayer', 'emptyCart', 'vendorGroups'],
          uigSavedCart: ['customSavedCartList', 'customSavedCartDetails', 'customSavedCartDialog', 'customSavedCartPage', 'uigAddToSavedCart']
        },
      },
    } as I18nConfig),
    {
      provide: CartPageLayoutHandler,
      useClass: UigCartPageLayoutHandler
    }
  ]
})
export class UigCartModule { }
